import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Container,
} from '@mui/material';
import { CheckCircle, Cancel, Email, Warning } from '@mui/icons-material';
import { mailBoxesApi } from 'src/api/mailBoxes';
import { toast } from 'react-hot-toast';

const MigrationApprovalPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('pending'); // pending, success, error, denied
  const [errorMessage, setErrorMessage] = useState('');
  const [previousOwner, setPreviousOwner] = useState(null);
  const [fetchingOwner, setFetchingOwner] = useState(false);

  const senderEmail = searchParams.get('senderEmail');
  const userEmail = searchParams.get('userEmail');
  const previousOwnerParam = searchParams.get('previousOwner');

  const fetchMailboxOwner = useCallback(async () => {
    if (!senderEmail) return;
    
    setFetchingOwner(true);
    try {
      // Try to get mailbox info to find the current owner
      // Include multiple possible fields that might contain owner info
      const mailboxes = await mailBoxesApi.getMailBoxes({
        filter: { senderEmail },
        projection: { 
          userEmail: 1, 
          senderEmail: 1,
          createdBy: 1,
          owner: 1,
          ownedBy: 1
        },
        limit: 1,
      });
      
      if (mailboxes && mailboxes.length > 0) {
        const mailbox = mailboxes[0];
        // Try different possible fields for owner email
        const ownerEmail = mailbox.userEmail || 
                         mailbox.createdBy || 
                         mailbox.owner || 
                         mailbox.ownedBy;
        
        if (ownerEmail) {
          setPreviousOwner(ownerEmail);
        } else {
          // If no owner field found, log for debugging
          console.log('Mailbox object:', mailbox);
          console.log('No owner field found in mailbox object');
          setPreviousOwner(null);
        }
      } else {
        console.log('No mailbox found for senderEmail:', senderEmail);
        setPreviousOwner(null);
      }
    } catch (error) {
      console.error('Failed to fetch mailbox owner:', error);
      setPreviousOwner(null);
    } finally {
      setFetchingOwner(false);
    }
  }, [senderEmail]);

  useEffect(() => {
    // Validate required parameters
    if (!senderEmail || !userEmail) {
      setStatus('error');
      setErrorMessage('Missing required parameters: senderEmail and userEmail are required.');
      return;
    }

    // Set previous owner from URL parameter if available
    if (previousOwnerParam) {
      setPreviousOwner(previousOwnerParam);
    } else {
      // Fetch the mailbox owner from the backend
      fetchMailboxOwner();
    }
  }, [senderEmail, userEmail, previousOwnerParam, fetchMailboxOwner]);

  const handleApprove = async () => {
    if (!senderEmail || !userEmail) {
      toast.error('Missing required information');
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      await mailBoxesApi.migrateMailbox({
        senderEmail,
        userEmail,
      });
      
      setStatus('success');
      toast.success('Mailbox migration completed successfully!');
    } catch (error) {
      setStatus('error');
      const errorMsg = error?.response?.data?.error || error?.message || 'Failed to approve migration';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeny = () => {
    setStatus('denied');
  };

  if (!senderEmail || !userEmail) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error">Invalid request. Missing required parameters.</Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={8}
          sx={{
            borderRadius: 3,
            padding: 4,
            textAlign: 'center',
            background: 'white',
          }}
        >
          {/* Logo/Header */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <img
              src="/static/media/b2b%20rocket%20logo%20blue%20cropped.8614f9d80530b572bac3.png"
              alt="B2B Rocket"
              style={{ height: '40px' }}
            />
          </Box>

          {/* Status-based content */}
          {status === 'pending' && (
            <>
              {/* Approval Icon */}
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                }}
              >
                <Email sx={{ fontSize: 32, color: 'white' }} />
              </Box>

              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#1a1a1a' }}>
                Mailbox Migration Approval
              </Typography>

              {/* Email Display */}
              <Box
                sx={{
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  padding: 2,
                  mb: 3,
                  textAlign: 'left',
                  position: 'relative',
                  pl: 3,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 2,
                    height: 18,
                    background: '#2563eb',
                    animation: 'blink 1s infinite',
                    '@keyframes blink': {
                      '0%, 50%': { opacity: 1 },
                      '51%, 100%': { opacity: 0 },
                    },
                  }}
                />
                <Typography variant="body1" sx={{ color: '#1a1a1a', fontWeight: 500 }}>
                  {senderEmail}
                </Typography>
              </Box>

              {/* Owner Info */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.5,
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Previous Owner
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 500 }}>
                    {fetchingOwner ? (
                      <CircularProgress size={14} sx={{ ml: 1 }} />
                    ) : (
                      previousOwner || previousOwnerParam || 'Unknown'
                    )}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.5,
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    New Owner
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 500 }}>
                    {userEmail}
                  </Typography>
                </Box>
              </Box>

              {/* Warning Box */}
              <Box
                sx={{
                  background: '#fef3c7',
                  border: '1px solid #fbbf24',
                  borderRadius: 2,
                  padding: 2,
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Warning sx={{ color: '#92400e', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#92400e', textAlign: 'left' }}>
                  Your access to this mailbox has been revoked
                </Typography>
              </Box>

              {/* Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeny}
                  disabled={loading}
                  sx={{ flex: 1, py: 1.5 }}
                >
                  Deny
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApprove}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
                  sx={{ flex: 1, py: 1.5 }}
                >
                  {loading ? 'Processing...' : 'Accept'}
                </Button>
              </Box>
            </>
          )}

          {status === 'success' && (
            <>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                }}
              >
                <CheckCircle sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#1a1a1a' }}>
                Mailbox Migration Success
              </Typography>
              <Typography variant="body1" sx={{ color: '#6b7280', mb: 3 }}>
                The mailbox has been successfully migrated to {userEmail}
              </Typography>
              {/* <Button
                variant="contained"
                onClick={() => navigate('/auth-main/login/v2')}
                sx={{ mt: 2 }}
              >
                Go to Dashboard
              </Button> */}
            </>
          )}

          {status === 'denied' && (
            <>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                }}
              >
                <Cancel sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#1a1a1a' }}>
                Migration Request Denied
              </Typography>
              <Typography variant="body1" sx={{ color: '#6b7280', mb: 3 }}>
                The migration request has been denied.
              </Typography>
              {/* <Button
                variant="outlined"
                onClick={() => navigate('/auth-main/login/v2')}
                sx={{ mt: 2 }}
              >
                Go to Dashboard
              </Button> */}
            </>
          )}

          {status === 'error' && (
            <>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                }}
              >
                <Cancel sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#1a1a1a' }}>
                Migration Failed
              </Typography>
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                {errorMessage || 'An error occurred during migration'}
              </Alert>
              {/* <Button
                variant="outlined"
                onClick={() => navigate('/auth-main/login/v2')}
                sx={{ mt: 2 }}
              >
                Go to Dashboard
              </Button> */}
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default MigrationApprovalPage;

